import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import {
  apiEditProductV2,
  apiGetGlobalTags,
  apiGetPropV2,
} from '../../helpers/api';
import {
  descriptionSchema,
  dimensionsSchema,
  getCategoryFields,
  getDescriptionFields,
  getLiveLinksFields,
  getDimensionsFields,
  getNameFields,
  getPriceFields,
  getSkuFields,
  getGtinFields,
  nameSchema,
  priceSchema,
  skuSchema,
} from './fieldsConfig';
import { useFilterDropdownOptions } from '../../hooks/useFilterDropdownOptions';
import { PRODUCT_FILTER_TYPE } from '../../constants/constants';
import { EditSection } from '../EditSection/EditSection';
import { useCallback, useMemo } from 'react';
import { EditPopupUI } from '../EditPopupUI/EditPopupUI';
import { useApi } from '../../hooks/useApi';
import { EditPopupButtonsUI } from '../EditPopupUI/EditPopupButtonsUI';
import { FormProvider, useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { errorToast, successToast } from '../../helpers/toastHelper';
import {
  editPropDetailsPopupActions,
  selectEditPropDetailsPopupData,
} from '../../redux/slicers/editPropDetailsPopupSlicer';
import { getTagsFields } from './fieldsConfig';
import { createGlobalTagAction } from '../../api/commonApi';
import { useUserContext } from '../../contexts/UserContext';
import { getIsRoofPropFields } from '../My3dModelDetailsPopup/fieldsConfig';
import { clearCacheFromAPI } from '../../helpers/jsHelper';

export const PropsEditDetailsPopupController = ({ onUpdateSuccess }) => {
  const dispatch = useDispatch();
  const product = useSelector(selectEditPropDetailsPopupData);
  const thumbnail = product?.thumbnail;
  const productId = product?.id;
  const show = Boolean(productId);
  const {organization} = useUserContext()

  const {
    data,
    loading,
    update: updateProductDetails,
  } = useApi({
    api: apiGetPropV2,
    id: productId,
    skip: !show,
  });

  if (!data) return null;

  const onClose = () => {
    dispatch(editPropDetailsPopupActions.close());
  };

  const handleUpdateSuccess = (response) => {
    updateProductDetails(response?.data);
    onUpdateSuccess(response?.data);
    successToast('Product is updated successfully.');
    onClose();
  };

  // if (!show) return null;

  return (
    <EditPropDetailsPopup
      productId={productId}
      data={data}
      show={show}
      onClose={onClose}
      loading={loading}
      thumbnail={thumbnail}
      onUpdateSuccess={handleUpdateSuccess}
    />
  );
};

const getSearchTagOption = (item) => ({
  value: typeof item === 'string' ? item : item.name,
  label: typeof item === 'string' ? item : item.name,
});

export const EditPropDetailsPopup = ({
  productId,
  data,
  show,
  onClose,
  loading,
  thumbnail,
  onUpdateSuccess,
}) => {
  const { sku_id } = data || {};
  const { isGuestUser, isEditor, isAdminOrOwner, currentUser } = useUserContext();
  const dispatch = useDispatch();
  const _productFilters = useFilterDropdownOptions({
    filterType: PRODUCT_FILTER_TYPE,
    fetchData: true,
  });

  // showing product page filters only to the Guest User & user with Editor access ( internal user )
  const productFilters = useMemo(() => {
    if (isEditor || isGuestUser()) {
      return [
        ..._productFilters?.filter((a) => !a?.is_product_page_filter),
        ..._productFilters?.filter((a) => a?.is_product_page_filter),
      ];
    }
    return _productFilters?.filter((a) => !a?.is_product_page_filter);
  }, [_productFilters, isGuestUser, isEditor]);

  const getFilterValue = (filterId) => {
    if (!data?.filteroptions || !productFilters) return [];

    const ids = data.filteroptions.map((o) => o.id);
    const filter = productFilters.find((filter) => filter.id === filterId);
    const value = filter.options.filter((category) => ids.includes(category.value));
    return value;
  };

  const memoizedSearch = useCallback(async (search, page) => {
    const response = await apiGetGlobalTags({ search, page });

    return {
      options: response.results.map(getSearchTagOption),
      hasMore: Boolean(response.next_link),
      additional: {
        page: page + 1,
      },
    };
  }, []);

  const loadSearchTagOptions = async (search, loadedOptions, { page }) => {
    return memoizedSearch(search, page);
  };

  const tagsValue = data?.tags.map(getSearchTagOption);

  const handleCreateTag = async (newTagName) => {
    const val = newTagName.trim();
    const result = await dispatch(createGlobalTagAction({ name: val }));
    if (!result?.data?.id) return;

    const existingTags = getValues('tags');
    const existingValues = existingTags.map((a) => a.value);

    setValue('tags', [...existingValues, result?.data?.name].map(getSearchTagOption));
  };

  const {
    isRoofPropFields,
    nameFields,
    skuFields,
    gtinFields,
    priceFields,
    descriptionFields,
    liveLinksFields,
    dimensionsFields,
    tagsFieldsConfig,
    defaultValues,
    schema,
  } = useMemo(() => {
    if (!data?.id) return {};

    let schemaObj = {
      ...nameSchema,
    };

    const isRoofPropFields = getIsRoofPropFields(data);
    const nameFields = getNameFields(data);
    const skuFields = getSkuFields(data);
    const gtinFields = getGtinFields(data);
    const priceFields = getPriceFields(data);
    const descriptionFields = getDescriptionFields(data);
    const liveLinksFields = getLiveLinksFields(data);
    const dimensionsFields = getDimensionsFields(data);

    isRoofPropFields[0].Component = ({control, setValue}) => {
      const fieldRegister = control?.register(isRoofPropFields?.[0]?.name);
      return <div className="d-flex gap-2 align-items-center text-muted">
        <input
        {...fieldRegister}
          className="form-check-input"
          type="checkbox"
          onChange={(e) => setValue("is_roof_prop", e.target.checked)}
        />
        <span className="mt-1">Roof Prop</span>
      </div>
    }

    const tagsFieldsConfig = getTagsFields(tagsValue, loadSearchTagOptions, handleCreateTag);

    const fields = [
      ...isRoofPropFields,
      ...nameFields,
      ...skuFields,
      ...gtinFields,
      ...priceFields,
      ...descriptionFields,
      ...liveLinksFields,
      ...dimensionsFields,
      ...tagsFieldsConfig.fieldsConfig,
    ];

    productFilters.map((filter) => {
      const { fieldsConfig, schema } = getCategoryFields(filter, getFilterValue(filter.id));
      fields.push(...fieldsConfig);
      schemaObj = { ...schemaObj, ...schema };
    });

    // fields.push(...tagsFieldsConfig.fieldsConfig)
    schemaObj = { ...schemaObj, ...tagsFieldsConfig.schema };

    const defaultValues = fields.reduce((acc, field) => {
      if (!field.name) return acc;

      acc[field.name] = field.value;
      return acc;
    }, {});

    return {
      isRoofPropFields,
      nameFields,
      skuFields,
      gtinFields,
      priceFields,
      descriptionFields,
      liveLinksFields,
      dimensionsFields,
      tagsFieldsConfig,
      defaultValues,
      schema: yup.object(schemaObj),
    };
  }, [data, data.tags, productFilters]);

  const methods = useForm({
    resolver: schema ? yupResolver(schema) : undefined,
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { isSubmitting, isDirty },
  } = methods;

  // useEffect(() => {
  //   reset(defaultValues);
  // }, [defaultValues])

  const onInvalid = () => {
    errorToast('Please fix errors', { toastId: 'TOAST_FIX_ERRORS' });
  };

  const onSubmit = async (values) => {
    if (!isDirty) {
      onClose();
      return;
    }

    const updatedData = { filteroptions: [], tags: [] };

    Object.keys(values).forEach((key) => {
      if (key.startsWith('filter_')) {
        const options = values[key];
        if(options) updatedData.filteroptions.push(...options.map((c) => c.value));
      } else if (key === 'tags') {
        const options = values[key];
        if(options) updatedData.tags.push(...options.map((c) => c.value));
      } else {
        const v = typeof values[key] === 'string' ? values[key]?.trim() : values[key];
        updatedData[key] = v || (v === false ? false : '');
      }
    });

    clearCacheFromAPI();

    const response = await apiEditProductV2({
      id: data.id,
      ...updatedData,
    });

    if (response) {
      onUpdateSuccess?.(response);
    }
  };

  return (
    <EditPopupUI
      show={show}
      onClose={onClose}
      loading={loading}
      title={data?.name}
      subtitle={sku_id}
      thumbnail={thumbnail}>
      {!loading && data && (
        <FormProvider {...methods}>
          <Form onSubmit={handleSubmit(onSubmit, onInvalid)} autoComplete="off">
            {/* name */}
            <EditSection title="Product name" fields={nameFields} />

            {/* sku */}
            <EditSection title="Product Sku ID" fields={skuFields} />

            {/* gtin */}
            <EditSection title="GTIN" fields={gtinFields} />

            {/* Roof Prop */}
            {data?.owner_organization === currentUser?.organization && <EditSection title="" fields={isRoofPropFields} />}

            {/* price */}
            <EditSection title="Price" fields={priceFields} />

            {/* Description */}
            <EditSection title="Description" fields={descriptionFields} />

            {/* Dimensions */}
            <EditSection title="Dimensions" fields={dimensionsFields} />

            {/* filters */}
            {productFilters.map((filter) => {
              const { fieldsConfig } = getCategoryFields(filter, getFilterValue(filter.id));
              return (
                <EditSection
                  key={filter.id}
                  title={filter.label_name}
                  fields={fieldsConfig}
                  previewMode="fields"
                />
              );
            })}

            {isEditor || isGuestUser() ? (
              <>
                {/* Live links */}
                <EditSection title="Live Links ( comma separated )" fields={liveLinksFields} />
              </>
            ) : null}

            {/* tags */}
            <EditSection title="Tags" fields={tagsFieldsConfig.fieldsConfig} previewMode="fields" />

            <EditPopupButtonsUI loading={isSubmitting} onCacel={onClose} />
          </Form>
        </FormProvider>
      )}
    </EditPopupUI>
  );
};
